import { createContext, useContext, useReducer, ReactNode } from 'react';
import { BiometricSession, BiometricStatus } from '@/types';

// Action types for biometric reducer
type BiometricAction =
  | { type: 'SESSION_START'; payload: { sessionId: string } }
  | { type: 'SESSION_UPDATE'; payload: Partial<BiometricSession> }
  | { type: 'SESSION_COMPLETE'; payload: BiometricSession }
  | { type: 'SESSION_ERROR'; payload: string }
  | { type: 'SESSION_RESET' };

// Biometric context type
interface BiometricContextType {
  session: BiometricSession | null;
  isActive: boolean;
  error: string | null;
  startSession: (sessionId: string) => void;
  updateSession: (updates: Partial<BiometricSession>) => void;
  completeSession: (result: BiometricSession) => void;
  setError: (error: string) => void;
  resetSession: () => void;
}

// Create context
const BiometricContext = createContext<BiometricContextType | undefined>(undefined);

// Biometric reducer
function biometricReducer(
  state: { session: BiometricSession | null; error: string | null },
  action: BiometricAction
): { session: BiometricSession | null; error: string | null } {
  switch (action.type) {
    case 'SESSION_START':
      return {
        session: {
          sessionId: action.payload.sessionId,
          status: 'initializing' as BiometricStatus,
        },
        error: null,
      };
    case 'SESSION_UPDATE':
      return {
        ...state,
        session: state.session ? { ...state.session, ...action.payload } : null,
      };
    case 'SESSION_COMPLETE':
      return {
        session: action.payload,
        error: null,
      };
    case 'SESSION_ERROR':
      return {
        ...state,
        error: action.payload,
      };
    case 'SESSION_RESET':
      return {
        session: null,
        error: null,
      };
    default:
      return state;
  }
}

// Provider component
interface BiometricProviderProps {
  children: ReactNode;
}

export function BiometricProvider({ children }: BiometricProviderProps) {
  const [{ session, error }, dispatch] = useReducer(biometricReducer, {
    session: null,
    error: null,
  });

  const startSession = (sessionId: string) => {
    dispatch({ type: 'SESSION_START', payload: { sessionId } });
  };

  const updateSession = (updates: Partial<BiometricSession>) => {
    dispatch({ type: 'SESSION_UPDATE', payload: updates });
  };

  const completeSession = (result: BiometricSession) => {
    dispatch({ type: 'SESSION_COMPLETE', payload: result });
  };

  const setError = (error: string) => {
    dispatch({ type: 'SESSION_ERROR', payload: error });
  };

  const resetSession = () => {
    dispatch({ type: 'SESSION_RESET' });
  };

  const value: BiometricContextType = {
    session,
    isActive: session !== null,
    error,
    startSession,
    updateSession,
    completeSession,
    setError,
    resetSession,
  };

  return (
    <BiometricContext.Provider value={value}>
      {children}
    </BiometricContext.Provider>
  );
}

// Hook to use biometric context
export function useBiometric(): BiometricContextType {
  const context = useContext(BiometricContext);
  if (context === undefined) {
    throw new Error('useBiometric must be used within a BiometricProvider');
  }
  return context;
}
